"use client";

import React from "react";
import { Box, Typography } from "@mui/material";
import Alert from "@/components/Alert";
import type { DiscontinuationProductSummary } from "./DiscontinuationInformationForm";
import { useLanguageContext } from "@/context/LanguageContext";
import { resolveDiscontinuationNoBuyerAssignedBannerMessage } from "@/components/ActiveProjects/shared/utils/discontinuationWorkflowModalLabels";

export const discontinuationProductSummaryLineSx = (darkMode: boolean) => ({
  fontFamily: '"Cabin", sans-serif',
  fontWeight: 400,
  fontSize: "16px",
  lineHeight: "25px",
  letterSpacing: 0,
  textTransform: "capitalize" as const,
  color: darkMode ? "#F8F8F2" : "#2C2C2C",
  margin: 0,
});

export const discontinuationProductSummarySkuSx = {
  color: "#A5684D",
};

export function hasDiscontinuationSummaryText(value?: string): boolean {
  const trimmed = String(value ?? "").trim();
  return trimmed !== "" && trimmed !== "—";
}

export type DiscontinuationProductSummaryViewProps = {
  productSummary: DiscontinuationProductSummary | null | undefined;
  darkMode: boolean;
  labels?: Partial<{
    product: string;
    vendor: string;
    buyerName: string;
  }>;
  /** Modal discontinue flow: warning when buyer is missing. */
  showNoBuyerAssignedBanner?: boolean;
  noBuyerAssignedBannerMessage?: string;
  showBuyerName?: boolean;
};

const defaultLabels = {
  product: "Product",
  vendor: "Vendor",
  buyerName: "Buyer Name",
};

const DiscontinuationProductSummaryView: React.FC<
  DiscontinuationProductSummaryViewProps
> = ({
  productSummary,
  darkMode,
  labels: labelsProp,
  showNoBuyerAssignedBanner = false,
  noBuyerAssignedBannerMessage,
  showBuyerName = true,
}) => {
  const { languageText } = useLanguageContext();
  const resolvedNoBuyerAssignedBannerMessage =
    noBuyerAssignedBannerMessage ??
    resolveDiscontinuationNoBuyerAssignedBannerMessage(
      languageText?.LABELS?.PACKAGING_AND_LABELLING
    );
  const labels = { ...defaultLabels, ...labelsProp };

  if (!productSummary) {
    return showNoBuyerAssignedBanner ? (
      <Box sx={{ "& .alert": { margin: 0 } }}>
        <Alert severity="warning" message={resolvedNoBuyerAssignedBannerMessage} />
      </Box>
    ) : null;
  }

  const showProductSummaryLine =
    hasDiscontinuationSummaryText(productSummary.productName) ||
    hasDiscontinuationSummaryText(productSummary.sku);
  const showVendorSummaryLine = hasDiscontinuationSummaryText(
    productSummary.vendorName
  );
  const showBuyerSummaryLine =
    showBuyerName && hasDiscontinuationSummaryText(productSummary.buyerName);
  const showNoBuyerBanner =
    showNoBuyerAssignedBanner &&
    !hasDiscontinuationSummaryText(productSummary.buyerName);

  const hasSummaryLines =
    showProductSummaryLine || showVendorSummaryLine || showBuyerSummaryLine;

  if (!hasSummaryLines && !showNoBuyerBanner) {
    return null;
  }

  return (
    <Box className="flex flex-col gap-[1.5rem]">
      {showNoBuyerBanner ? (
        <Box sx={{ "& .alert": { margin: 0 } }}>
          <Alert severity="warning" message={resolvedNoBuyerAssignedBannerMessage} />
        </Box>
      ) : null}
      {hasSummaryLines ? (
        <Box className="flex flex-col">
          {showProductSummaryLine ? (
            <Typography
              component="p"
              sx={discontinuationProductSummaryLineSx(darkMode)}
            >
              {labels.product}:
              {hasDiscontinuationSummaryText(productSummary.productName) ? (
                <> {productSummary.productName}</>
              ) : null}
              {hasDiscontinuationSummaryText(productSummary.sku) ? (
                <>
                  {hasDiscontinuationSummaryText(productSummary.productName)
                    ? " "
                    : " "}
                  <Box component="span" sx={discontinuationProductSummarySkuSx}>
                    {productSummary.sku}
                  </Box>
                </>
              ) : null}
            </Typography>
          ) : null}
          {showVendorSummaryLine ? (
            <Typography
              component="p"
              sx={discontinuationProductSummaryLineSx(darkMode)}
            >
              {labels.vendor}: {productSummary.vendorName}
            </Typography>
          ) : null}
          {showBuyerSummaryLine ? (
            <Typography
              component="p"
              sx={discontinuationProductSummaryLineSx(darkMode)}
            >
              {labels.buyerName}: {productSummary.buyerName}
            </Typography>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};

export default DiscontinuationProductSummaryView;
